import styled, { keyframes } from 'styled-components';
import { Layout, Card, Button, Drawer, Alert } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { IToken, ThemeList } from 'store/ducks/globalTypes';
import { variablesList } from 'constants/variablesList';
import { IThemeSelectorProps } from 'components/ThemeSelector/ThemeSelector';
import CustomSelect from 'components/CustomSelect';
import {
  OverlayProps,
  SectionProps,
  SidebarProps,
  StyledCardProps,
} from 'store/ducks/Global/types';

const { Header } = Layout;
const shake = keyframes`
  0% { transform: translateX(0); }
  10% { transform: translateX(-5px); }
  20% { transform: translateX(5px); }
  30% { transform: translateX(-5px); }
  40% { transform: translateX(5px); }
  50% { transform: translateX(-5px); }
  60% { transform: translateX(5px); }
  70% { transform: translateX(-5px); }
  80% { transform: translateX(5px); }
  90% { transform: translateX(-5px); }
  100% { transform: translateX(0); }
`;

export const StyledLayout = styled(Layout)<IToken>`
  min-height: 100vh;

  > .ant-layout {
    min-height: initial;

    .ant-layout-header {
      height: fit-content;
    }
  }

  aside {
    padding-right: ${(props) => `${props.token.paddingLG}px`};
    flex: initial !important;
    text-align: center;
    max-width: fit-content !important;
    min-width: fit-content !important;
    width: fit-content !important;
    position: relative !important;
    right: 0px;
  }

  h4 {
    text-align: center;
    font-size: 24px;
    font-weight: 700;
    margin: 0;
    padding-right: 34px;
  }
`;

export const AntDrawer = styled(Drawer)`
  .ant-drawer-mask {
    background-color: red;
  }
  .ant-picker {
    margin-top: 10px;
    min-width: 162px;
  }
`;

export const HorizontalLayout = styled('div')<IThemeSelectorProps>`
  .ant-tabs-nav-list {
    width: -webkit-fill-available;
    margin-bottom: 1rem;

    .ant-tabs-tab {
      width: -webkit-fill-available;
      display: table;
    }
  }
`;

export const HorizontalLayoutMain = styled('div')<IThemeSelectorProps>`
  @media (max-width: 1400px) {
    padding: 24px;
  }
  .ant-tabs-nav-list {
    width: -webkit-fill-available;
    margin-bottom: 1rem;

    .ant-tabs-tab {
      width: -webkit-fill-available;
      display: table;
    }
  }
`;

export const StyledHeader = styled(Header)<IToken>`
  display: flex;
  flex-direction: row-reverse;
  gap: 20px;
  padding-inline: ${(props) => `${props.token.paddingLG}px`};
  line-height: ${(props) => `${props.token.colorBgContainerDisabled}px`};
`;
export const GridContainer = styled('div')<{ token?: any }>`
  display: flex;
  column-gap: 16px;
  row-gap: 48px;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 60px;
  position: relative; /* for absolute lines */

  > a {
    height: fit-content;
    width: 350px;
  }

  .ant-card {
    width: 300px;

    .ant-card-meta-title {
      white-space: pre-line !important;
      font-size: 16px;

      button {
        span {
          font-size: 18px;
        }
      }
    }

    .ant-card-body {
      cursor: pointer;
    }
  }

  .vertical-line {
    position: absolute;
    left: 50%;
    top: 50%;
    width: 2px;
    height: 100%;
    background-color: ${(props) => `${props.token.colorBgSpotlight}`};

    @media (max-width: 1021px) {
      display: none;
    }
  }

  .horizontal-first {
    position: absolute;
    top: 50%;
    width: calc(100% * 1.5);
    height: 2px;
    background-color: ${(props) => `${props.token.colorBgSpotlight}`};

    @media (max-width: 1021px) {
      display: none;
    }
  }

  .horizontal-specific {
    position: absolute;
    height: 2px;
    background-color: ${(props) => `${props.token.colorBgSpotlight}`};
    right: 27%;

    @media (max-width: 1021px) {
      display: none;
    }
  }
`;

export const Container = styled('div')`
  padding: 0px;
`;

export const ReportCard = styled(Card)<{ token: any }>`
  width: 100%;
  min-height: 240px;
  border: none;
  border-radius: 4px;

  background: linear-gradient(
    307deg,
    ${({ token }) => token.colorBgSpotlight} 84%,
    ${({ token }) => token.actionButtons} 95%
  );
  color: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 20px;
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;
  box-shadow: 0 0px 3px 2px ${({ token }) => token.colorBgSpotlight};

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 0px 3px 4px ${({ token }) => token.colorBgSpotlight};
  }

  .ant-card-body {
    padding: 0 !important;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
  }

  .ant-card-meta-title {
    font-size: 18px;
    font-weight: 700;
    color: #ffffff;
  }

  .ant-card-meta-description {
    font-size: 17px;
    color: rgba(255, 255, 255, 0.85);
  }

  .ant-btn {
    width: -webkit-fill-available;
    height: 100px;
    text-align: center;
    text-wrap: auto;

    padding: 24px;
    background: rgba(255, 255, 255, 0.15);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    color: #fff;
    text-align: center;
  }
`;

export const StyledButton = styled(Button).attrs((props) => ({
  disabled: props.disabled,
}))`
  width: 110px;
  align-self: center;
  display: flex;
  justify-content: center;
  height: auto;
  margin-top: 20px;

  &:disabled {
    color: rgb(0 0 0 / 79%);
    background: #f0ebeb;
  }
`;

export const FilterContainer = styled('div')`
  display: flex;
  flex-direction: column;
  margin-top: 23px;
  gap: 8px;
`;

export const OpenCloseButton = styled(Button)`
  width: 30px;
  text-transform: uppercase;
  align-self: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  height: fit-content;
`;

export const ExcelDownload = styled.div`
  margin-bottom: 3rem;

  button {
    width: auto;
  }
`;

export const StepsComponent = styled('div')<{ token: any }>`
  padding: 0px;
  position: relative;
  padding-top: 10px;

  .ant-steps {
    display: flex;
    flex-wrap: wrap;
  }
  .ant-steps-item-active {
    .ant-steps-item-content{
      background-color: ${({ token }) => token.colorPrimaryBgHover}  !important;
    }
  }
  .ant-steps-item {
    flex: none;
    gap: 10;
    padding-inline-start: 0 !important;
    padding-top: 0;
    margin: 0 4px 8px;
    background-color: ${({ token }) => token.colorPrimary};

    .ant-steps-item-icon {
      border-radius: 4px;
      cursor: pointer;
      display: none;
      flex: none;
    }

    .ant-steps-item-content {
      background-color: ${({ token }) => token.colorPrimary};
    }
      border-radius: 4px;
    }

    .ant-steps-item-title {
      font-size: 15px;
      color: #fff !important;
      font-weight: 600;
      padding-inline-end: 5px !important;
      padding: 0 10px;
    }

    .ant-steps-item-title::after {
      display: none !important;
    }

  }
  .ant-empty-description {
    color: black !important;
  }

  .ant-steps-icon {
    color: #fff !important;
  }
  .ant-steps-item-container:hover .ant-steps-icon {
    color: #fff !important;
  }
`;

export const AlertComponent = styled('div')`
  padding-inline: 24px;
`;

export const AlertInfo = styled(Alert)`
  float: right;
  position: absolute;
  right: 88px;
  top: -96px;
  background-color: rgb(172 6 92);
  color: white;
  border: 1px solid rgb(172 6 92);
  animation: iwrNJy 3s ease-out;
  border-radius: 5px;
  .ant-alert-description {
    color: white;
  }
  .ant-alert-close-icon {
    color: white;
  }
  animation: ${shake} 3s ease-out;
`;

export const AlertInfoReporting = styled(Alert)`
  float: right;
  max-width: 530px;
  margin: 10px 0;
  position: relative;
  font-weight: 900;
  background-color: rgb(172 6 92);
  color: white;
  border: 1px solid rgb(172 6 92);
  .ant-alert-description {
    color: white;
  }
  .ant-alert-close-icon {
    color: white;
  }
  animation: ${shake} 3s ease-out;
`;

export const AlertInfoForTPP = styled(Alert)`
  max-width: 222px;
  font-weight: 900;
  background-color: rgb(172 6 92);
  border: 1px solid rgb(172 6 92);
  position: absolute;
  padding: 11px;
  margin-top: 10px;
  .ant-alert-description {
    color: white;
  }
  .ant-alert-close-icon {
    color: white;
  }
  animation: ${shake} 3s ease-out;
`;

export const SelectionOfTPP = styled.div``;

export const StepButtons = styled.div`
  display: flex;
  gap: 8px;

  div {
    display: flex;
    gap: 14px;
  }
  position: relative;

  div {
    position: absolute;
    top: 32px;
    left: 14px;
    z-index: 1;
  }
`;

export const WorkingAreaButton = styled.div<{ token: any }>`
  display: flex;
  margin: 0 24px;
  justify-content: end;
  gap: 20px;
  position: relative;
  top: -50px;

  @media (max-width: 1640px) {
    top: 10px;
  }

  button {
    background: ${({ token }) => token.actionButtons};
    width: 193px;
    white-space: pre-wrap;

    span {
      @media (max-width: 768px) {
        font-size: 12px;
      }
    }

    &:hover {
      background-color: ${({ token }) => token.actionButtonsHover} !important;
    }
  }

  div {
    // display: flex;
    gap: 10px;
    // text-align: justify;
  }
`;

export const SelectionArea = styled.div<{ token: any }>`
  display: flex;
  margin: 0 24px;
  justify-content: end;
  gap: 20px;
  position: relative;
  top: -50px;

  @media (max-width: 1640px) {
    top: 10px;
  }

  button {
    background: ${({ token }) => token.actionButtons};
    width: 193px;
    white-space: pre-wrap;

    @media (max-width: 400px) {
      width: 111px;
    }

    span {
      @media (max-width: 768px) {
        font-size: 12px;
      }
    }

    &:hover {
      background-color: ${({ token }) => token.actionButtonsHover} !important;
    }
  }

  div {
    gap: 10px;
  }
`;

export const SelectionButtons = styled('div')`
  display: flex;
  flex-direction: column;
  position: absolute;
  right: 270px;
  top: -40px;

  @media (max-width: 1640px) {
    top: -84px;
  }

  @media (max-width: 768px) {
    top: -78px;
    right: 0;
  }

  @media (max-width: 413px) {
    top: 0;
    left: 0;
  }
`;

export const SelectionButtonsForLayout = styled('div')`
  display: flex;
  flex-direction: column;
  position: absolute;
  right: 300px;
  top: -39px;

  @media (max-width: 1640px) {
    top: -84px;
  }

  @media (max-width: 768px) {
    top: -84px;
    left: 0;
  }

  @media (max-width: 697px) {
    top: -38px;
  }
  @media (max-width: 413px) {
    left: 0;
  }
`;

export const SelectFilter = styled(CustomSelect)`
  margin: 0;

  .ant-select-selector {
    width: 162px !important;
  }
`;

export const SelectWithTopMargin = styled('div')`
  margin-top: 16px;
`;

export const DataEntryTitle = styled.span`
  margin: 0;
  font-weight: bold;
  display: block;
  font-size: 22px;
`;

export const TitleContainer = styled('div')<IThemeSelectorProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  background: linear-gradient(
    90deg,
    ${(props) => variablesList[props.theme as ThemeList].tabSelected},
    ${(props) => variablesList[props.theme as ThemeList].buttonPrimaryBgHover}
  );
  padding: 10px 20px;
  border-radius: 12px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  margin-bottom: 20px;
`;

export const TitleText = styled.h1`
  font-size: 2.5rem;
  font-weight: bold;
  color: white;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.4);
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 1.5px;
`;

export const SubtitleText = styled.p`
  font-size: 1rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.9);
  margin: 5px 0 0 0;
`;

export const SelectedFilters = styled('div')<
  StyledCardProps,
  IThemeSelectorProps
>`
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ token }) => token.colorBgSpotlight};
  padding: 20px;
  border-radius: 4px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  width: fit-content;
  margin: auto;
  color: #fff;
  font-weight: 700;
  font-size: 16px;

  @media (max-width: 1320px) {
    margin: inherit;
  }

  h2 {
    margin: 0;
  }
`;

export const AreaTitle = styled('div')`
  width: fit-content;
  margin: auto;
  position: absolute;
  left: 50%;
  -ms-transform: translate(-50%, -50%);
  transform: translate(-50%, 0%);
`;

export const BranchInfo = styled('p')`
  color: rgb(172 6 92);
  font-size: 18px;
`;

export const TooltipSelection = styled('div')``;

export const Selection = styled('div')`
  font-size: 16px;
  font-weight: bold;
`;

export const Sidebar = styled.div<SidebarProps>`
  position: fixed;
  top: 0;
  height: 100vh;
  background: white;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.2);
  padding: 20px;
  transition: transform 0.3s ease-in-out;
  transform: ${({ open }) => (open ? 'translateX(20px)' : 'translateX(110%)')};
  z-index: 1000;
`;

export const Overlay = styled.div<OverlayProps>`
  display: ${({ show }) => (show ? 'block' : 'none')};
  position: fixed;
  top: 0;
  right: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.4);
  z-index: 100;
`;

export const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ExclamationIcon = styled(ExclamationCircleOutlined)`
  color: red;
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const ConsiderModalBtn = styled(Button)`
  z-index: 100;
`;

export const Section = styled('div')<SectionProps>`
  padding: 0px 16px;
  margin: 40px 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 6px;
    height: 100%;
    border-radius: 4px;

    background: ${({ section, token }) => {
      if (section.includes('Format')) return token?.colorPrimaryBgHover;
      if (section.includes('Reports')) return token?.colorPrimary;
    }};
  }
`;

export const SectionTitle = styled('h2')<{ token: any }>`
  font-weight: 900;
  text-align: center;
  margin: 0;
  color: ${({ token }) => token.colorPrimaryBgHover} !important;
  font-size: 18px;

  @media (max-width: 450px) {
    font-size: 12px;
  }
`;

export const List = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  padding: 0 16px;

  @media (max-width: 600px) {
    gap: 12px;
  }
`;

export const CardLine = styled('a')<IThemeSelectorProps>`
  display: flex;
  align-items: center;
  border-radius: 12px;
  text-decoration: none;
  transition: all 0.2s ease;
  border-radius: 4px;

  button {
    display: flex;
    align-items: center;
    background: linear-gradient(
      307deg,
      ${(props) => variablesList[props.theme as ThemeList].defaultTabBg} 90%,
      ${(props) => variablesList[props.theme as ThemeList].tabSelected} 95%
    );
    padding: 16px 24px;
    text-decoration: none;
    box-shadow: rgba(0, 0, 0, 0.6) 5px 5px 12px;
    transition: all 0.2s ease;
    border-radius: 4px;
    width: 341px;
    margin-top: 0;
    margin-bottom: 10px;

    span {
      text-wrap-mode: wrap;
    }

    &:hover {
      transform: translateX(4px);
    }
  }

  .ant-btn-primary:not(:disabled):hover {
    background-color: transparent;
    background: linear-gradient(
      307deg,
      ${(props) => variablesList[props.theme as ThemeList].defaultTabBg} 90%,
      ${(props) => variablesList[props.theme as ThemeList].tabSelected} 95%
    );
  }

  @media (max-width: 600px) {
    padding: 12px 16px;
  }
`;

export const LineText = styled('span')`
  font-size: 1rem;
  font-weight: 500;

  @media (max-width: 600px) {
    font-size: 0.95rem;
  }
`;

export const ExportComponent = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 1rem;
`;

export const TableWrapper = styled.div`
  overflow-x: auto;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: white;
`;

export const Thead = styled.thead`
  background-color: #f5f5f5;
`;

export const Th = styled.th`
  text-align: left;
  padding: 0.75rem;
  font-weight: 600;
  border: 1px solid #ddd;
`;

export const ThWidth = styled(Th)`
  width: 30%;
`;

export const Td = styled.td`
  padding: 0.75rem;
  border: 1px solid #ddd;
  vertical-align: top;
`;

export const Tr = styled.tr`
  &:nth-child(even) {
    background-color: #fafafa;
  }
`;

export const RootLayout = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
`;

export const MainLayout = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
`;

export const FilterSidebar = styled.div`
  width: 320px;
  min-width: 320px;
  background: #ffffff;
  border-right: 1px solid #f0f0f0;
  padding: 20px;
  overflow-y: auto;
`;

export const FilterContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const FiltersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
`;

export const ContentArea = styled.div`
  padding-left: 24px;
  padding-right: 24px;
`;

// filters
export const FilterCard = styled.div`
  border-radius: 4px;
  padding: 16px 32px;
  display: flex;
  flex-direction: column;

  @media (max-width: 1024px) {
    padding: 12px 24px;
  }
`;

export const FilterHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const FilterTitle = styled.h3<{ theme?: string }>`
  font-size: 15px;
  font-weight: 600;
  color: ${(props) =>
    variablesList[props.theme as ThemeList]?.filterLabelColor || '#111827'};
  margin: 0;
  letter-spacing: 0.2px;
`;

export const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 48px 80px;
  border: 1px solid #f3f4f6;
  padding: 6px 12px;

  @media (max-width: 1024px) {
    gap: 32px 60px;
  }
`;

export const FilterItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const FilterLabel = styled.label<{ theme?: string }>`
  font-size: 13px;
  font-weight: 500;
  color: ${(props) =>
    variablesList[props.theme as ThemeList]?.filterLabelColor || '#4b5563'};
  margin-bottom: 4px;
`;

export const ReactFlowDiv = styled.div`
  width: 100%;
  height: calc(100vh - 140px);
  min-height: 400px;
  position: relative;

  .react-flow {
    width: 100% !important;
    height: 100% !important;
  }

  .react-flow__pane {
    cursor: grab;
  }

  .react-flow__pane:active {
    cursor: grabbing;
  }

  @media (max-width: 768px) {
    height: calc(100vh - 120px);
  }

  @media (max-width: 480px) {
    height: calc(100vh - 100px);
  }
`;

export const ModalContent = styled('div')`
  line-height: 1.6;
`;

export const ErrorText = styled('span')`
  color: red;
`;

export const FullWidthDatePicker = styled('div')`
  .ant-picker {
    width: 100%;
  }
`;

export const SmallDatePicker = styled('div')`
  .ant-picker {
    margin-top: 10px;
    width: 162px;
  }
`;

export const CardItemWrapper = styled('div')`
  position: relative;

  .ant-card-meta-detail {
    padding: 10px;
  }
`;

export const ButtonsRow = styled('div')`
  display: flex;
  justify-content: center;
`;

export const StepContent = styled('div')`
  text-align: center;
  color: #aaa;
  background-color: #f0f2f5;
  border-radius: 8px;
  border: 1px solid #d9d9d9;
  margin-top: 16px;
`;
